"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowUp, Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { getSiteGlueOnboardingHrefWithWebsite } from "~/lib/siteglue/cta-config";
import { analyzeWebsiteForHire } from "~/lib/marketplace/analyze-website-for-hire";
import { normalizeMarketplaceWebsiteUrl } from "~/lib/marketplace/website-suggestions";
import type { WebsiteAgentSuggestion } from "~/lib/marketplace/website-suggestions";
import {
  SITEGLUE_ROLE_TEMPLATES,
  siteGlueRoleDoes,
  siteGlueRoleTemplateById,
  type SiteGlueRoleTemplateId,
} from "~/lib/siteglue/role-builder-prompts";
import {
  ROLE_CHANNEL_OPTIONS,
  ROLE_WEBSITE_CHANNEL_IDS,
  channelsFromEngagementAnswers,
  roleDedicatedPhoneQuestion,
  roleEmailInquiryQuestion,
  roleWebsiteEngagementQuestion,
  type RoleChannelId,
} from "~/lib/siteglue/role-setup-steps";
import { cn } from "~/lib/utils";

type ChatTurn = {
  id: string;
  from: "visitor" | "trish";
  text: string;
};

type Phase = "idle" | "summary" | "web" | "phone" | "email" | "url" | "review";

type HeroTrishRoleChatProps = {
  className?: string;
};

const ROLE_FIELD_HINTS = [
  "Build a Front Desk Receptionist trained to answer phone calls, engage website visitors, and take a message when the team is away...",
  "Build an Online Patient Coordinator for my website that welcomes patients, answers service questions, and helps them request an appointment...",
  "Build an Inbound Sales Rep for my website that answers questions, gathers information and connects visitors with the right person at our company...",
  "Build a Customer Support Agent for my website that answers published questions and connects people with the right teammate...",
  "Build an Online Sales Assistant for my website that helps visitors compare services, answers pricing questions, and points them to the next step...",
] as const;

function useRotatingFieldHint(active: boolean): string {
  const [index, setIndex] = useState(0);
  const [count, setCount] = useState(0);
  const full = ROLE_FIELD_HINTS[index] ?? "";

  useEffect(() => {
    if (!active) return;
    if (count < full.length) {
      const timer = window.setTimeout(() => setCount((value) => value + 1), 18);
      return () => window.clearTimeout(timer);
    }
    const timer = window.setTimeout(() => {
      setCount(0);
      setIndex((value) => (value + 1) % ROLE_FIELD_HINTS.length);
    }, 1600);
    return () => window.clearTimeout(timer);
  }, [active, count, full.length, index]);

  return full.slice(0, count);
}

function locationSummary(suggestion: WebsiteAgentSuggestion): string {
  const list = suggestion.recommendations?.locationList?.filter(Boolean) ?? [];
  if (list.length) return list.slice(0, 3).join(", ");
  const text = suggestion.recommendations?.locations?.trim();
  return text || "Location details will be confirmed during setup";
}

function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

export function HeroTrishRoleChat({ className }: HeroTrishRoleChatProps) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState("");
  const [roleId, setRoleId] = useState<string | null>(null);
  const [roleLabel, setRoleLabel] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [summaryFull, setSummaryFull] = useState("");
  const [summaryCount, setSummaryCount] = useState(0);
  const [channels, setChannels] = useState<RoleChannelId[]>([]);
  const [webChannels, setWebChannels] = useState<RoleChannelId[]>([]);
  const [wantsPhone, setWantsPhone] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [suggestion, setSuggestion] = useState<WebsiteAgentSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const summaryCommitted = useRef(false);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const showHint = phase === "idle" && !draft && !analyzing;
  const hint = useRotatingFieldHint(showHint);
  const showComposer = phase === "idle" || phase === "url";

  const push = (turn: Omit<ChatTurn, "id">) => {
    setTurns((prev) => [...prev, { ...turn, id: `${prev.length}-${turn.from}` }]);
  };

  useEffect(() => {
    if (phase !== "summary" || !summaryFull || !roleLabel) return;
    if (summaryCount < summaryFull.length) {
      const step = summaryFull.length > 400 ? 6 : 1;
      const timer = window.setTimeout(
        () => setSummaryCount((value) => Math.min(summaryFull.length, value + step)),
        12,
      );
      return () => window.clearTimeout(timer);
    }
    if (summaryCommitted.current) return;
    const timer = window.setTimeout(() => {
      summaryCommitted.current = true;
      setTurns((prev) => [
        ...prev,
        { id: `${prev.length}-trish`, from: "trish", text: summaryFull },
        {
          id: `${prev.length + 1}-trish`,
          from: "trish",
          text: roleWebsiteEngagementQuestion(roleId, roleLabel),
        },
      ]);
      setPhase("web");
    }, 280);
    return () => window.clearTimeout(timer);
  }, [phase, roleId, roleLabel, summaryCount, summaryFull]);

  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight });
  }, [turns, summaryCount, phase]);

  const beginRole = (label: string, id: string | null, visitorText?: string) => {
    setRoleId(id);
    setRoleLabel(label);
    setSuggestion(null);
    setError(null);
    setChannels([]);
    setWebChannels([]);
    setWantsPhone(false);
    setSummaryFull(siteGlueRoleDoes(id, label));
    setSummaryCount(0);
    summaryCommitted.current = false;
    setTurns(
      visitorText
        ? [{ id: "0-visitor", from: "visitor", text: visitorText }]
        : [],
    );
    setPhase("summary");
  };

  const selectTemplate = (id: SiteGlueRoleTemplateId) => {
    if (analyzing || roleId === id) return;
    const template = siteGlueRoleTemplateById(id);
    if (!template) return;
    beginRole(template.label, template.id);
  };

  const confirmWebsite = () => {
    if (!roleLabel) return;
    const labels = ROLE_CHANNEL_OPTIONS.filter((option) => webChannels.includes(option.id)).map(
      (option) => option.label,
    );
    push({ from: "visitor", text: labels.length ? joinLabels(labels) : "Not on the website" });
    push({ from: "trish", text: roleDedicatedPhoneQuestion(roleId, roleLabel) });
    setPhase("phone");
  };

  const answerPhone = (yes: boolean) => {
    if (!roleLabel) return;
    setWantsPhone(yes);
    push({ from: "visitor", text: yes ? "Yes" : "No" });
    push({ from: "trish", text: roleEmailInquiryQuestion(roleId, roleLabel) });
    setPhase("email");
  };

  const answerEmail = (yes: boolean) => {
    if (!roleLabel) return;
    const next = channelsFromEngagementAnswers({
      website: webChannels,
      phone: wantsPhone,
      email: yes,
    });
    if (!next.length) {
      setError("Choose at least one way for this employee to engage: website, phone, or email.");
      push({ from: "visitor", text: "No" });
      push({
        from: "trish",
        text: `${roleWebsiteEngagementQuestion(roleId, roleLabel)} Pick at least one, or say yes to phone or email.`,
      });
      setPhase("web");
      return;
    }
    setError(null);
    setChannels(next);
    push({ from: "visitor", text: yes ? "Yes" : "No" });
    push({ from: "trish", text: "What is the business website?" });
    setPhase("url");
  };

  const analyze = async (raw: string) => {
    const normalized = normalizeMarketplaceWebsiteUrl(raw);
    if (!normalized) {
      setError("Enter a website such as yourcompany.com.");
      return;
    }
    setError(null);
    setAnalyzing(true);
    push({ from: "visitor", text: normalized });
    push({ from: "trish", text: "I'm reading the site now." });
    try {
      const result = await analyzeWebsiteForHire(normalized, { skipOnboardingAlert: true });
      setSuggestion(result);
      setPhase("review");
      const locations = locationSummary(result);
      push({
        from: "trish",
        text: `Here's what I found. Business: ${result.businessName}. What they do: ${result.summary || result.categoryLabel}. Locations: ${locations}. Role: ${roleLabel}. Approve this summary before I create anything.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "I couldn't read that website.";
      setError(message);
      push({
        from: "trish",
        text: `${message} Send the website again and I'll try once more. Nothing is created until you approve a summary.`,
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || analyzing) return;
    setDraft("");
    if (phase === "idle") {
      beginRole(text, "custom", text);
      return;
    }
    if (phase === "url") void analyze(text);
  };

  const approve = () => {
    if (!suggestion || !roleId || !channels.length) return;
    const href = getSiteGlueOnboardingHrefWithWebsite(suggestion.websiteUrl, {
      role: roleId,
      roleLabel: roleLabel ?? undefined,
      company: suggestion.businessName,
      approved: true,
      roleChannels: channels,
    });
    window.location.assign(href);
  };

  const streamedSummary =
    phase === "summary" ? summaryFull.slice(0, summaryCount) : "";

  return (
    <div className={cn("w-full text-left", className)} data-testid="hero-trish-role-chat">
      {turns.length > 0 || streamedSummary ? (
        <div ref={transcriptRef} className="mb-3 max-h-96 space-y-2 overflow-y-auto" aria-live="polite">
          {turns.map((turn) => (
            <div
              key={turn.id}
              className={cn(
                "max-w-[95%] rounded-2xl px-3 py-2 text-sm leading-relaxed",
                turn.from === "trish"
                  ? "whitespace-pre-wrap bg-white text-slate-700"
                  : "ml-auto bg-[#4f81bd] text-white",
              )}
            >
              <p>{turn.text}</p>
            </div>
          ))}
          {streamedSummary ? (
            <div className="max-w-[95%] whitespace-pre-wrap rounded-2xl bg-white px-3 py-2 text-sm leading-relaxed text-slate-700">
              <p>
                {streamedSummary}
                <span className="ml-0.5 inline-block h-4 w-px animate-pulse bg-slate-400" />
              </p>
            </div>
          ) : null}
          {analyzing ? (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Reading the website
            </p>
          ) : null}
        </div>
      ) : null}

      {phase === "web" ? (
        <div className="mx-auto mt-1 max-w-md">
          <div
            className="flex flex-col gap-2"
            role="group"
            aria-label={roleWebsiteEngagementQuestion(roleId, roleLabel ?? undefined)}
          >
            {ROLE_CHANNEL_OPTIONS.filter((option) =>
              (ROLE_WEBSITE_CHANNEL_IDS as readonly string[]).includes(option.id),
            ).map((option) => {
              const selected = webChannels.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() =>
                    setWebChannels((prev) =>
                      prev.includes(option.id)
                        ? prev.filter((id) => id !== option.id)
                        : [...prev, option.id],
                    )
                  }
                  className={cn(
                    "rounded-xl border px-4 py-2.5 text-left text-sm",
                    selected
                      ? "border-[#4f81bd] bg-[#4f81bd]/10 text-slate-900"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300",
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <Button
            type="button"
            className="mt-3 h-11 w-full bg-[#4f81bd] hover:bg-[#3d6fa8]"
            onClick={confirmWebsite}
          >
            Continue
          </Button>
        </div>
      ) : null}

      {phase === "phone" || phase === "email" ? (
        <div className="mx-auto mt-1 flex max-w-md gap-2">
          <Button
            type="button"
            className="h-11 flex-1 bg-[#4f81bd] hover:bg-[#3d6fa8]"
            onClick={() => (phase === "phone" ? answerPhone(true) : answerEmail(true))}
          >
            Yes
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 flex-1"
            onClick={() => (phase === "phone" ? answerPhone(false) : answerEmail(false))}
          >
            No
          </Button>
        </div>
      ) : null}

      {showComposer ? (
        <form onSubmit={onSubmit}>
          <div className="relative rounded-2xl border border-slate-200/80 bg-[#f3f3f1] px-4 py-3 shadow-sm">
            {showHint ? (
              <p
                aria-hidden
                className="pointer-events-none min-h-[4.5rem] whitespace-normal pr-12 text-left text-sm leading-relaxed text-slate-400"
              >
                {hint}
                <span className="ml-0.5 inline-block h-4 w-px animate-pulse bg-slate-400" />
              </p>
            ) : null}
            {phase === "url" ? (
              <input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="yourcompany.com"
                aria-label="Business website"
                disabled={analyzing}
                className="h-8 w-full bg-transparent pr-12 text-sm text-slate-900 outline-none placeholder:text-slate-400"
              />
            ) : (
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                  }
                }}
                rows={3}
                aria-label={hint || "Describe the role you want to hire"}
                disabled={analyzing}
                className={cn(
                  "w-full resize-none bg-transparent pr-12 text-sm leading-relaxed text-slate-900 outline-none",
                  showHint ? "absolute inset-0 h-full px-4 py-3 opacity-0" : "min-h-[4.5rem]",
                )}
              />
            )}
            <button
              type="submit"
              disabled={analyzing || !draft.trim()}
              aria-label="Send"
              className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-slate-500 transition hover:bg-slate-300 disabled:opacity-50"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
        </form>
      ) : null}
      {error ? <p className="mt-2 text-center text-sm text-red-600">{error}</p> : null}
      {suggestion ? (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-left text-sm text-slate-700">
          <p className="font-semibold text-slate-900">Approve this summary</p>
          <ul className="mt-2 space-y-1">
            <li>Business: {suggestion.businessName}</li>
            <li>What they do: {suggestion.summary || suggestion.categoryLabel}</li>
            <li>Locations: {locationSummary(suggestion)}</li>
            <li>Role: {roleLabel}</li>
          </ul>
          <Button
            type="button"
            className="mt-3 bg-[#4f81bd] hover:bg-[#3d6fa8]"
            onClick={approve}
          >
            Approve and create account
          </Button>
          <p className="mt-2 text-xs text-slate-500">
            A SiteGlue account is required after you approve. Nothing is created before that.
          </p>
        </div>
      ) : null}

      <div className="mt-5">
        <p className="text-center text-xs text-slate-500">Try role template</p>
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          {SITEGLUE_ROLE_TEMPLATES.map((role) => (
            <button
              key={role.id}
              type="button"
              onClick={() => selectTemplate(role.id)}
              className={cn(
                "rounded-lg border px-3 py-1.5 text-sm transition",
                roleId === role.id
                  ? "border-slate-900 bg-white text-slate-900"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-300",
              )}
            >
              {role.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
